import { endOfDay, format, parse, parseISO, startOfDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { ETimeAggregationTypes, TTimeAggregationTypes } from '@enums/common';

import { timezoneUTC } from '@constants/common';

export const MINUTE_DURATION_MS = 60_000;
export const HOUR_DURATION_MS = 3_600_000;
export const currentLocationOffset = new Date().getTimezoneOffset();

export const isValidDate = (date: any): boolean => {
	const newDate = new Date(date);

	return (date instanceof Date || newDate instanceof Date) && !isNaN(date);
};

export const parseStringDate = (date: string, template = 'dd/MM/yyyy'): Date => {
	return parse(date, template, new Date());
};

export const parseStringDateStartOfTheDay = (date: string, template = 'dd/MM/yyyy'): number => {
	return startOfDayInMilliseconds(parseStringDate(date, template));
};

export const formatDate = (date: Date | number | string, template = 'dd/MM/yyyy', options?: any): string => {
	try {
		return format(typeof date === 'string' ? parseISO(date) : date, template, options);
	} catch (e) {
		return 'Invalid Date';
	}
};

export const dateFormattingWithSlash = (date: any): string => formatDate(date, 'dd/MM/yyyy hh:mm a');

export const startOfDayInMilliseconds = (date: number | Date): number => startOfDay(date).getTime();

export const endOfDayInMilliseconds = (date: number | Date): number => endOfDay(date).getTime();

export const convertLocalMsToUTCms = (localMilliseconds: number): number | null => {
	if (!localMilliseconds) return null;
	return (localMilliseconds - currentLocationOffset * MINUTE_DURATION_MS);
};

export const convertMinutesToMilliseconds = (minutes: number): number => minutes * MINUTE_DURATION_MS;

export const convertHoursToMilliseconds = (hours: number): number => hours * HOUR_DURATION_MS;

export const isSameDayInUTC = (date1: Date | number, date2: Date | number): boolean => {
	const newDate1 = new Date(date1);
	const newDate2 = new Date(date2);

	const year1 = newDate1.getUTCFullYear();
	const month1 = newDate1.getUTCMonth();
	const day1 = newDate1.getUTCDate();

	const year2 = newDate2.getUTCFullYear();
	const month2 = newDate2.getUTCMonth();
	const day2 = newDate2.getUTCDate();

	return year1 === year2 && month1 === month2 && day1 === day2;
};

export const generateDurationString = (minutes: number) => {
	if (!minutes || typeof minutes !== 'number') return '0 minutes';

	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	const remainingMinutes = minutes % 60;
	const parts = [];

	if (days) parts.push(`${days} day${days > 1 ? 's' : ''}`);
	if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
	if (remainingMinutes) parts.push(`${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`);

	return parts.join(' ') || '0 minutes';
};

const getDateFormatTemplate = (
	timeAggregationType: TTimeAggregationTypes,
	separator: string,
): string => {
	switch (timeAggregationType) {
		case ETimeAggregationTypes.seconds:
			return 'ss';
		case ETimeAggregationTypes.minutes:
			return 'mm';
		case ETimeAggregationTypes.hours:
			return 'HH';
		case ETimeAggregationTypes.days:
			return `dd${separator}MM`;
		case ETimeAggregationTypes.months:
			return `MM${separator}yyyy`;
		case ETimeAggregationTypes.hourMinutes:
		case ETimeAggregationTypes.interval:
			return 'HH:mm';
		case ETimeAggregationTypes.dayMonthYear:
		default:
			return `dd${separator}MM${separator}yyyy`;
	}
};

export const formatDateByAggregationType = ({
	date,
	timeAggregationType,
	organizationTimezone,
	separator = '/',
}: {
	date: number;
	timeAggregationType: TTimeAggregationTypes;
	organizationTimezone: string;
	separator?: string;
}): string => {
	const formatTemplate = getDateFormatTemplate(timeAggregationType, separator);
	const zonedTime = utcToZonedTime(date, organizationTimezone);
	return formatDate(zonedTime, formatTemplate, { timeZone: organizationTimezone });
};

export const dateFormattingUTCDayMonthYear = (date: number): string => {
	return formatDateByAggregationType({
		date: startOfDayInUTC(new Date(date)),
		organizationTimezone: timezoneUTC,
		timeAggregationType: ETimeAggregationTypes.dayMonthYear,
		separator: '.',
	});
};
